// The People & roles lens of the analysis screen: each node is a role's run of consecutive actions, from when work
// reaches it to when it hands the work over to another role, and the edges are those hand-overs. A role appears once
// for each separate run, so the graph reads as the process collapsed by who does the work. Actions no role performs
// (done by systems) make runs of their own. Each run carries how its work splits between models (drawn as a band) and
// how busy its role is across all its work (from the workforce report). Built from the analysis graph, so it can be
// tested without a page
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { RoleSummary, WORKFORCE_STATUS_LABELS, WorkforceReport, WorkforceStatus } from '../workforce/workforce-model';
import type { LensGraphEdge, LensGraphNode } from './lens-node-style';
import { actionCount, buildRuns, Handover, LensOutput, LensRun, outputsGraph, stepsFact } from './runs';

export type { Handover, LensSegment } from './runs';

export interface RoleRun extends LensRun {
    roleKeys: string[];                 // the roles performing it; none when systems do
    automated: boolean;                 // no role performs it
    // the (first) role's figures across all its work, from the workforce report
    external: boolean;
    headcount: number;
    capacity: number | null;
    utilisation: number | null;
    status: WorkforceStatus;
    capacityFromDefault: boolean;
}

export interface RoleLens {
    runs: RoleRun[];
    handovers: Handover[];
    outputs: LensOutput[];
    outputLinks: Handover[];            // from the runs that create them
}

// who performs an action: its roles, or the systems that do it when no role does
const performers = (a: AnalysisNode) => {
    const roles = (a.roles || []).map(r => r._key).sort();
    return roles.length ? { key: `role:${roles.join('+')}`, roles, systems: [] as string[] } : { key: `system:${[...(a.systems || [])].sort().join('+')}`, roles, systems: [...(a.systems || [])] };
};

export const buildRolesLens = (analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] }, report: WorkforceReport, systemNames: Map<string, string>,
    modelKeys?: Set<string>): RoleLens => {
    const roles = new Map<string, RoleSummary>(report.roles.map(r => [r.key, r]));
    const { runs, handovers, outputs, outputLinks } = buildRuns(analysis, a => performers(a).key, modelKeys);
    return {
        runs: runs.map(run => {
            const who = performers(run.actions[0]);
            const role = who.roles.length ? roles.get(who.roles[0]) : undefined;
            const name = who.roles.length
                ? who.roles.map(r => roles.get(r)?.name || 'Unnamed role').join(' & ')
                : who.systems.length ? who.systems.map(s => systemNames.get(s) || 'Unnamed system').join(' & ') : 'No one assigned';
            return {
                key: run.key, roleKeys: who.roles, name, automated: !who.roles.length, actionKeys: run.actions.map(a => a._key),
                actionNames: run.actions.map(a => a.name || 'Unnamed action'), hours: run.hours, segments: run.segments,
                external: !!role?.external, headcount: role?.headcount || 0, capacity: role?.capacity ?? null, utilisation: role?.utilisation ?? null,
                status: role?.status || 'unknown', capacityFromDefault: !!role?.capacityFromDefault,
            };
        }),
        handovers, outputs, outputLinks,
    };
};

// how the cards are filled: by their role's status, by how much work they hold, or not at all
export type RolesColourBy = 'status' | 'hours' | 'none';

// light tints of the status colours, so a card's text stays readable; the status dot keeps the full colour
const STATUS_TINTS: Partial<Record<WorkforceStatus, string>> = { over: '#fde8e8', stretched: '#fdebd9', healthy: '#e3f5e3', under: '#e0ecfb' };
// the single-hue ramp the heatmaps use, light (little) to dark (most)
export const HOURS_RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];

// the graph a roles lens draws: a card for each run and an edge for each hand-over
export const rolesLensGraph = (lens: RoleLens, modelColours: Map<string, string>, colourBy: RolesColourBy, format: { hours: (v: number) => string; count: (v: number) => string }):
    { nodes: LensGraphNode[]; edges: LensGraphEdge[] } => {
    const maxHours = Math.max(0, ...lens.runs.map(r => r.hours));
    const fill = (run: RoleRun) => {
        if (colourBy === 'status') return run.automated ? undefined : STATUS_TINTS[run.status];
        // by the square root of the share of the most, so one very busy run doesn't leave the rest all the lightest shade
        if (colourBy === 'hours' && maxHours > 0) return HOURS_RAMP[Math.min(HOURS_RAMP.length - 1, Math.floor(Math.sqrt(run.hours / maxHours) * HOURS_RAMP.length))];
        return undefined;
    };
    const names = new Map(lens.runs.map(r => [r.key, r.name]));
    const nodes = lens.runs.map(run => {
        const actions = actionCount(run.actionKeys.length);
        const status = run.automated ? 'Done by systems' : `${WORKFORCE_STATUS_LABELS[run.status]}${run.utilisation !== null ? `, ${Math.round(run.utilisation * 100)}% of capacity across all its work` : ''}`;
        return {
            key: run.key, label: run.name, dashed: run.automated, status: run.automated ? 'none' : run.status, fill: fill(run),
            detail: run.automated ? `${actions} · automated` : `${actions} · ${format.hours(run.hours)} hours a year`,
            segments: run.segments.map(s => ({ colour: modelColours.get(s.modelKey) || '#a9a8a3', share: s.share })),
            tooltip: `${run.name}: ${run.actionNames.join(' → ')}. ${status}`,
            tip: {
                kind: run.automated ? 'Automated work' : run.external ? 'External organisation' : 'Role',
                title: run.name,
                facts: [
                    { label: 'Steps', value: stepsFact(run.actionNames) },
                    ...(run.automated ? [{ label: 'How', value: 'Done by systems: no role performs it' }]
                        : run.external ? [{ label: 'Status', value: "Outside the organisation: its capacity isn't ours to manage" }] : [{ label: 'Status', value: status }]),
                    ...(run.headcount ? [{ label: 'People', value: String(run.headcount) }] : []),
                ],
                figures: [
                    { label: 'Hours', value: format.hours(run.hours), note: 'a year, in these steps' },
                    { label: 'Actions', value: String(run.actionKeys.length) },
                    ...(run.capacity ? [{ label: 'Capacity', value: format.hours(run.capacity), note: `hours a year${run.capacityFromDefault ? ' (the full-time default)' : ''}` }] : []),
                    ...(run.utilisation !== null && !run.automated ? [{ label: 'Across all its work', value: `${Math.round(run.utilisation * 100)}%`, note: 'of capacity' }] : []),
                    ...(run.segments.length > 1 ? [{ label: 'Models', value: String(run.segments.length) }] : []),
                ],
            },
        };
    });
    const edges = lens.handovers.map(h => ({
        key: h.key, from: h.from, to: h.to, weight: h.items,
        tooltip: `${names.get(h.from)} hands ${format.count(h.items)} items a year to ${names.get(h.to)}`,
    }));
    const outputs = outputsGraph(lens.outputs, lens.outputLinks, names, modelColours, format.count);
    return { nodes: [...nodes, ...outputs.nodes], edges: [...edges, ...outputs.edges] };
};
